import {
  Modal,
  Stack,
  Group,
  Text,
  Badge,
  Divider,
  Button,
  Table,
  Paper,
  Title,
  Box,
} from '@mantine/core';
import { IconEdit, IconTrash, IconBox } from '@tabler/icons-react';
import type { Person } from '@/types/models';

interface PersonDetailsModalProps {
  opened: boolean;
  onClose: () => void;
  person: any; // Using any because of the joined data structure
  onEdit: (person: Person) => void;
  onDelete: (person: Person) => void;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: '35% 1fr',
        gap: '1rem',
        alignItems: 'start',
      }}
    >
      <Box pt={4}>
        <Text size="sm" c="dimmed" fw={500} style={{ textAlign: 'right' }}>
          {label}
        </Text>
      </Box>
      <Box pt={4}>
        <Text size="sm">{value || '-'}</Text>
      </Box>
    </Box>
  );
}

export function PersonDetailsModal({
  opened,
  onClose,
  person,
  onEdit,
  onDelete,
}: PersonDetailsModalProps) {
  if (!person) return null;

  const assignedAssets = person.person_assets || [];

  return (
    <Modal opened={opened} onClose={onClose} title="Person Details" size="lg">
      <Stack gap="md">
        <Group justify="apart">
          <div>
            <Text size="xl" fw={700}>
              {person.full_name}
            </Text>
            {person.title && (
              <Text size="sm" c="dimmed">
                {person.title}
              </Text>
            )}
          </div>
          {person.department && <Badge variant="light">{person.department.name}</Badge>}
        </Group>

        <Divider />

        <Stack gap="sm">
          <Text size="sm" fw={700} tt="uppercase" c="dimmed">
            Contact Information
          </Text>
          <DetailRow label="Email" value={person.email} />
          <DetailRow label="Phone" value={person.phone} />
          <DetailRow label="Employee ID" value={person.employee_id} />
        </Stack>

        <Divider />

        <Stack gap="sm">
          <Text size="sm" fw={700} tt="uppercase" c="dimmed">
            Organization
          </Text>
          <DetailRow label="Department" value={person.department?.name} />
          <DetailRow label="Location" value={person.location?.name} />
        </Stack>

        {person.notes && (
          <>
            <Divider />
            <Stack gap="sm">
              <Text size="sm" fw={700} tt="uppercase" c="dimmed">
                Notes
              </Text>
              <Text size="sm">{person.notes}</Text>
            </Stack>
          </>
        )}

        <Divider />

        <Stack gap="md">
          <Group>
            <IconBox size={20} />
            <Title order={4}>Assigned Assets ({assignedAssets.length})</Title>
          </Group>

          {assignedAssets.length > 0 ? (
            <Paper withBorder>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Tag</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Assigned</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {assignedAssets.map((pa: any) => (
                    <Table.Tr key={pa.id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {pa.asset.tag_id}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <div>
                          <Text size="sm">{pa.asset.description}</Text>
                          <Text size="xs" c="dimmed">
                            {pa.asset.brand} {pa.asset.model}
                          </Text>
                        </div>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={pa.asset.status === 'Assigned' ? 'blue' : 'gray'}
                          variant="light"
                        >
                          {pa.asset.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {new Date(pa.assigned_at).toLocaleDateString()}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          ) : (
            <Paper withBorder p="md">
              <Text size="sm" c="dimmed" ta="center">
                No assets assigned to this person
              </Text>
            </Paper>
          )}
        </Stack>

        <Divider />

        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={() => {
              onDelete(person);
              onClose();
            }}
          >
            Delete
          </Button>
          <Button
            leftSection={<IconEdit size={16} />}
            onClick={() => {
              onEdit(person);
              onClose();
            }}
          >
            Edit
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
